import React, { useState, useEffect, useLayoutEffect, useRef, createContext, useContext } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
// NotificationBell is rendered only in DashboardLayout — sub-account
// shells inherit alerts via the workspace bell (with sub_account_id
// surfaced in the body). See `utils/notify.py` for the discipline.
import {
  LayoutDashboard,
  Lock,
  Bot,
  BookOpen,
  Wrench,
  Phone,
  Calendar,
  Settings,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Link2,
  Headphones,
  Mic,
  MessageSquare,
  Zap,
  GitBranch,
  Sun,
  Moon,
  Users,
  AlertTriangle,
  ExternalLink,
  X,
  ShieldOff,
  Coins,
  Infinity,
  Palette,
} from 'lucide-react';

import { LogoImage } from './DashboardLayout';
import { Menu } from 'lucide-react';

const SubAccountContext = createContext();

export const useSubAccount = () => useContext(SubAccountContext);

const SubAccountLayout = ({ children, fillContent = false }) => {
  const { user, api } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const location = useLocation();
  const contentRef = useRef(null);
  const isInbox = /\/conversations\/?$/.test(location.pathname);
  useLayoutEffect(() => {
    if (isInbox && contentRef.current) contentRef.current.scrollTop = 0;
  }, [isInbox]);
  const navigate = useNavigate();
  const { workspaceId, subAccountId } = useParams();
  
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [subAccount, setSubAccount] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [branding, setBranding] = useState(null);
  const [ghlStatus, setGhlStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [balanceStatus, setBalanceStatus] = useState(null);
  const [balanceBannerDismissed, setBalanceBannerDismissed] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [isUncapped, setIsUncapped] = useState(false); // Per-sub-account "no cap" set by agency
  const [lowCreditBalance, setLowCreditBalance] = useState(false);
  const [zeroCreditBalance, setZeroCreditBalance] = useState(false);
  const [creditBannerDismissed, setCreditBannerDismissed] = useState(false);

  useEffect(() => {
    const savedCollapsed = localStorage.getItem('subAccountSidebarCollapsed');
    if (savedCollapsed) {
      setCollapsed(JSON.parse(savedCollapsed));
    }
  }, []);

  useEffect(() => {
    if (subAccountId && workspaceId) {
      fetchData();
    }
  }, [subAccountId, workspaceId]);

  const fetchData = async () => {
    try {
      const [subAccResponse, wsResponse, ghlResponse, brandingResponse] = await Promise.all([
        api.get(`/api/sub-accounts/${subAccountId}`),
        api.get(`/api/workspaces/${workspaceId}`),
        api.get(`/api/sub-accounts/${subAccountId}/ghl/status`).catch(() => ({ data: null })),
        api.get(`/api/workspaces/${workspaceId}/branding`).catch(() => ({ data: null }))
      ]);
      setSubAccount(subAccResponse.data);
      setWorkspace(wsResponse.data);
      setGhlStatus(ghlResponse.data);
      setBranding(brandingResponse.data);
    } catch (error) {
      toast.error('Failed to load sub-account');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Balance check on mount + every 2 minutes
  const fetchBalanceStatus = async () => {
    try {
      const resp = await api.get(`/api/sub-accounts/${subAccountId}/balance-status`);
      setBalanceStatus(resp.data);
      // Reset dismiss if balance goes to zero (force visibility for critical)
      if (resp.data?.calls_blocked) {
        setBalanceBannerDismissed(false);
      }
    } catch (err) {
      // Silently fail — don't block UI
    }
  };

  useEffect(() => {
    if (subAccountId) {
      fetchBalanceStatus();
      fetchCreditBalance();
      const interval = setInterval(() => {
        fetchBalanceStatus();
        fetchCreditBalance();
      }, 120000);
      return () => clearInterval(interval);
    }
  }, [subAccountId]);

  const fetchCreditBalance = async () => {
    try {
      const resp = await api.get(`/api/sub-accounts/${subAccountId}/credits/balance`);
      const credits = resp.data?.credits ?? null;
      const unlimited = resp.data?.unlimited ?? false;
      const uncapped = resp.data?.balance_unlimited ?? false;
      setCreditBalance(credits);
      setIsUnlimited(unlimited);
      setIsUncapped(uncapped);
      if (!unlimited && credits !== null) {
        setZeroCreditBalance(credits <= 0);
        setLowCreditBalance(credits > 0 && credits <= 3);
        if (credits <= 0) setCreditBannerDismissed(false);
      } else {
        setZeroCreditBalance(false);
        setLowCreditBalance(false);
      }
    } catch (err) {
      // Silently fail
    }
  };

  const toggleCollapse = () => {
    const newValue = !collapsed;
    setCollapsed(newValue);
    localStorage.setItem('subAccountSidebarCollapsed', JSON.stringify(newValue));
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '' },
    { icon: Palette, label: 'Brand Profiles', path: '/brand-profiles' },
    { icon: BookOpen, label: 'AI Architect', path: '/project-manager' },
    { icon: MessageSquare, label: 'Inbox', path: '/conversations' },
    { icon: Bot, label: 'AI Agents', path: '/agents' },
    { icon: BookOpen, label: 'Knowledge Base', path: '/knowledge' },
    // Functions sidebar entry hidden in iter132 (feature removed per user spec).
    // Page file/route preserved so direct-URL access still works for any
    // legacy bookmarks — safe to fully delete in a later cleanup pass.
    // { icon: Wrench, label: 'Functions', path: '/functions' },
    { icon: Phone, label: 'Phone Numbers', path: '/phone-numbers' },
    { icon: GitBranch, label: 'Call Routing', path: '/call-routing' },
    { icon: Users, label: 'Contact Timeline', path: '/contacts' },
    { icon: Calendar, label: 'Callbacks & Textbacks', path: '/scheduled-calls' },
    // Widgets — Orb + Chat Widget creators (Phase 1: config + embed snippet)
    { icon: Sparkles, label: 'Widgets', path: '/widgets' },
    { icon: Settings, label: 'Settings', path: '/settings' },
  ];

  const basePath = `/workspace/${workspaceId}/sub-account/${subAccountId}`;

  const isActive = (path) => {
    const fullPath = basePath + path;
    if (path === '') {
      return location.pathname === basePath || location.pathname === basePath + '/';
    }
    return location.pathname.startsWith(fullPath);
  };

  // Theme classes - Deep Space aesthetic
  const bgMain = isDarkMode ? 'bg-[#08090d]' : 'bg-gray-50';
  const sidebarBg = isDarkMode ? 'bg-[#0c0d12]/95 backdrop-blur-xl' : 'bg-white/95 backdrop-blur-xl';
  const borderColor = isDarkMode ? 'border-white/[0.08]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const hoverBg = isDarkMode ? 'hover:bg-white/[0.05]' : 'hover:bg-gray-100';

  if (loading) {
    return (
      <div className={`min-h-screen ${bgMain} flex items-center justify-center`}>
        <div className="relative">
          <div className="w-14 h-14 border-4 border-teal-400/20 border-t-teal-400 rounded-full animate-spin" />
          <div className="absolute inset-0 w-14 h-14 border-4 border-transparent border-r-violet-400/40 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
        </div>
      </div>
    );
  }

  if (subAccount?.status === 'archived') {
    return (
      <div className={`min-h-screen ${bgMain} flex items-center justify-center p-6`} data-testid="sub-account-locked-screen">
        <div className="max-w-md text-center space-y-4">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-amber-400/15 flex items-center justify-center"><Lock className="text-amber-400" size={22} /></div>
          <h1 className={`text-2xl font-semibold ${textPrimary}`}>This sub-account is locked</h1>
          <p className={`text-sm ${textSecondary}`}>{subAccount.name} is archived. Calls, chats and all features are turned off. Restore it from Sub-Accounts to use it again.</p>
          <div className="flex justify-center gap-3">
            <button onClick={() => navigate(`/workspace/${workspaceId}/sub-accounts`)} className={`px-4 py-2 rounded-xl text-sm border border-white/10 ${textPrimary} ${hoverBg}`} data-testid="locked-screen-back-btn">Back to Sub-Accounts</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SubAccountContext.Provider value={{ subAccount, workspace, refreshData: fetchData, balanceStatus, refreshBalance: fetchBalanceStatus }}>
      <div className={`h-screen ${bgMain} flex overflow-hidden`}>
        {mobileNavOpen && <button className="fixed inset-0 z-40 bg-black/60 md:hidden" onClick={() => setMobileNavOpen(false)} aria-label="Close navigation" data-testid="subaccount-mobile-nav-backdrop"/>}
        {/* Sidebar */}
        <aside 
          data-testid="subaccount-sidebar"
          onClick={e => { if (e.target.closest('a')) setMobileNavOpen(false); }}
          className={`fixed left-0 top-0 h-full ${sidebarBg} border-r ${borderColor} flex flex-col z-50 transition-[transform,width] duration-300 ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 ${
            collapsed ? 'w-[72px]' : 'w-64'
          }`}
        >
          {/* Header - Logo + Back + Sub-Account Info */}
          <div className={`p-4 border-b ${borderColor}`}>
            {/* Logo — inherits parent workspace whitelabel branding when enabled */}
            {branding?.whitelabel_enabled && branding?.logo_data ? (
              <div className="flex justify-center mb-3" data-testid="subaccount-whitelabel-logo">
                <LogoImage logoData={branding.logo_data} businessName={branding.business_name} collapsed={collapsed} />
              </div>
            ) : !collapsed ? (
              <div className="mb-3">
                <img src={isDarkMode ? '/voxinity-logo.png?v=8' : '/voxinity-logo-light.png?v=8'} alt="Voxinity AI" className="h-8 w-auto" />
              </div>
            ) : (
              <div className="flex justify-center mb-3">
                <img src={isDarkMode ? '/voxinity-icon.png?v=8' : '/voxinity-icon-light.png?v=8'} alt="Voxinity AI" className="h-7 w-auto" />
              </div>
            )}
            
            {!collapsed ? (
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-400 to-violet-500 flex items-center justify-center text-white font-bold shadow-lg shadow-teal-400/20">
                    {subAccount?.name?.charAt(0) || 'S'}
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-[#0c0d12]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${textPrimary} truncate`}>{subAccount?.name}</p>
                  <p className="text-xs text-teal-400 font-medium tracking-wide">SUB-ACCOUNT</p>
                </div>
              </div>
            ) : (
              <div className="flex justify-center">
                <div className="relative">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-400 to-violet-500 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-teal-400/20">
                    {subAccount?.name?.charAt(0) || 'S'}
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-[#0c0d12]" />
                </div>
              </div>
            )}
          </div>

          {/* LeadConnector Connection Status */}
          {!collapsed && (
            <div className={`px-4 py-3 border-b ${borderColor}`}>
              <div className={`flex items-center gap-2 px-3 py-2.5 rounded-xl ${
                ghlStatus?.connected 
                  ? isDarkMode ? 'bg-green-400/10 border border-green-400/20' : 'bg-green-50 border border-green-200'
                  : ghlStatus?.needs_reconnect
                  ? isDarkMode ? 'bg-red-400/10 border border-red-400/20' : 'bg-red-50 border border-red-200'
                  : isDarkMode ? 'bg-white/[0.03] border border-white/[0.06]' : 'bg-gray-50 border border-gray-200'
              }`}>
                <Link2 size={14} className={ghlStatus?.connected ? 'text-green-400' : ghlStatus?.needs_reconnect ? 'text-red-400' : textSecondary} />
                <div className="flex flex-col min-w-0">
                  <span className={`text-xs font-medium ${ghlStatus?.connected ? 'text-green-400' : ghlStatus?.needs_reconnect ? 'text-red-400' : textSecondary}`}>
                    {ghlStatus?.connected ? 'CRM Connected' : ghlStatus?.needs_reconnect ? 'CRM Expired' : 'CRM Not Connected'}
                  </span>
                  {ghlStatus?.connected && ghlStatus?.location_name && (
                    <span className="text-[10px] text-green-400/70 truncate">
                      {ghlStatus.location_name}
                    </span>
                  )}
                  {ghlStatus?.needs_reconnect && (
                    <span className="text-[10px] text-red-400/70">Reconnect required</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <Link
                  key={item.path}
                  to={basePath + item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group relative ${
                    active
                      ? 'bg-gradient-to-r from-teal-400/10 to-violet-400/10 text-teal-400'
                      : `${textSecondary} ${hoverBg}`
                  } ${collapsed ? 'justify-center' : ''}`}
                  title={collapsed ? item.label : undefined}
                >
                  {active && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-gradient-to-b from-teal-400 to-violet-400 rounded-r-full" />
                  )}
                  <Icon size={20} className={active ? 'text-teal-400' : `${textSecondary} group-hover:text-teal-400 transition-colors`} />
                  {!collapsed && <span className={`text-sm font-medium ${active ? 'text-teal-400' : ''}`}>{item.label}</span>}
                  {!collapsed && item.underConstruction && (
                    <span
                      className="ml-auto text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300 border border-amber-400/30"
                      title="Under construction — Phase 1: editor + embed snippet only"
                    >
                      WIP
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Credit Balance */}
          <div className={`py-2 border-t ${borderColor} ${collapsed ? 'px-1 flex justify-center' : 'px-3'}`}>
            <button
              onClick={() => !isUnlimited && navigate(`${basePath}/settings?tab=billing`)}
              className={`${collapsed ? 'w-10 h-10 flex items-center justify-center rounded-xl' : 'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl'} transition-all group ${
                isUnlimited
                  ? isDarkMode ? 'bg-gradient-to-r from-amber-400/[0.06] to-orange-400/[0.06] border border-amber-400/20' : 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200'
                  : isDarkMode ? 'bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08]' : 'bg-gray-100 hover:bg-gray-200 border border-gray-200'
              }`}
              data-testid="credit-balance-btn"
              style={isUnlimited ? { cursor: 'default' } : {}}
              title={collapsed ? (isUnlimited ? 'Balance: Unlimited' : `Balance: $${creditBalance ?? '---'}`) : undefined}
            >
              <div className={`${collapsed ? '' : 'w-7 h-7 rounded-lg'} flex items-center justify-center shrink-0 ${collapsed ? '' : isUnlimited ? 'bg-gradient-to-br from-amber-400/30 to-orange-500/30' : 'bg-gradient-to-br from-amber-400/20 to-orange-500/20'}`}>
                {isUnlimited ? <Infinity size={collapsed ? 18 : 14} className="text-amber-400" /> : <Coins size={collapsed ? 18 : 14} className="text-amber-400" />}
              </div>
              {!collapsed && (
                <div className="text-left min-w-0 flex-1">
                  <p className={`text-[10px] uppercase tracking-wider ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Balance</p>
                  {isUnlimited ? (
                    <div className="flex items-center gap-1.5">
                      <Infinity size={18} className={`${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                      <span className={`text-xs font-medium ${isDarkMode ? 'text-amber-400/70' : 'text-amber-600/70'}`}>Unlimited</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className={`text-sm font-bold tabular-nums ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                        ${creditBalance !== null ? Number(creditBalance).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '---'}
                      </p>
                      {isUncapped && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-teal-400/15 text-teal-300 font-bold uppercase tracking-wider flex items-center gap-0.5" title="Agency lets this sub-account draw from the pool with no cap">
                          <Infinity size={9} /> No Limit
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
              {!collapsed && !isUnlimited && (
                <div className="flex items-center">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-400/10 text-amber-400 font-medium">+</span>
                </div>
              )}
            </button>
          </div>

          {/* Collapse Toggle */}
          <div className={`p-3 border-t ${borderColor}`}>
            <button
              onClick={toggleCollapse}
              className={`w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl ${textSecondary} ${hoverBg} transition-colors`}
            >
              {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
              {!collapsed && <span className="text-sm">Collapse</span>}
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className={`flex-1 min-w-0 transition-[margin] duration-300 h-full flex flex-col overflow-hidden ml-0 ${collapsed ? 'md:ml-[72px]' : 'md:ml-64'}`}>
          {/* Top Bar */}
          <div className={`shrink-0 z-40 ${isDarkMode ? 'bg-[#08090d]/80' : 'bg-white/80'} backdrop-blur-xl border-b ${borderColor}`}>
            <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3">
              <button className={`md:hidden p-2 rounded-md ${textPrimary}`} aria-label="Open navigation" aria-expanded={mobileNavOpen} data-testid="subaccount-mobile-nav-toggle" onClick={() => { setCollapsed(false); setMobileNavOpen(true); }}><Menu size={20}/></button>
              {/* Breadcrumb */}
              <div className="flex flex-1 min-w-0 items-center gap-2">
                <span data-testid="subaccount-header-name" className={`text-sm font-medium break-all ${textPrimary}`}>{subAccount?.name}</span>
              </div>
              {/* Actions */}
              <div className="flex items-center gap-2">
                {/* Notification bell is intentionally NOT rendered inside
                    the sub-account shell — it lives only at the agency
                    workspace level (DashboardLayout). Sub-account-level
                    alerts (low balance, GHL disconnect, telephony error,
                    etc.) are surfaced to the workspace owner via the
                    workspace bell with the sub-account identified in the
                    notification body. */}
                <button
                  onClick={toggleTheme}
                  className={`p-2 rounded-lg ${textSecondary} ${hoverBg} transition-colors`}
                  data-testid="sub-account-theme-toggle"
                  title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                >
                  {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
                </button>
              </div>
            </div>
          </div>
          {/* ────────────────────────────────────────────────────────── */}
          {/* Unified Balance Banner — collapses all 4 legacy banners    */}
          {/* (voxinity zero/low + provider zero/low) into ONE clean     */}
          {/* stack so operators aren't spammed by parallel warnings.    */}
          {/* ────────────────────────────────────────────────────────── */}
          <AnimatePresence>
            {(() => {
              const alerts = [];
              // Hard stops go first (red).
              if (!isUnlimited && zeroCreditBalance) {
                alerts.push({
                  id: 'credit-zero',
                  level: 'critical',
                  icon: Coins,
                  testId: 'credit-zero-banner',
                  title: 'Balance depleted',
                  body: 'All AI services (calls, SMS, chat & orb widgets, test sessions) are paused until you add funds.',
                  cta: { label: 'Add Balance', testId: 'credit-add-btn-zero',
                         onClick: () => navigate(`${basePath}/billing`) },
                });
              }
              if (balanceStatus?.calls_blocked) {
                const prov = (balanceStatus.provider || 'Provider').charAt(0).toUpperCase() + (balanceStatus.provider || '').slice(1);
                alerts.push({
                  id: 'provider-zero',
                  level: 'critical',
                  icon: ShieldOff,
                  testId: 'balance-blocked-banner',
                  title: `${prov} balance is $0.00`,
                  body: 'Phone calls are disabled until you top up your telephony provider balance.',
                  cta: balanceStatus.top_up_url ? {
                    label: 'Add Funds',
                    testId: 'balance-topup-btn',
                    href: balanceStatus.top_up_url,
                  } : null,
                });
              }
              // Soft warnings (amber). Only if no critical on same topic.
              if (!isUnlimited && lowCreditBalance && !zeroCreditBalance && !creditBannerDismissed) {
                alerts.push({
                  id: 'credit-low',
                  level: 'warn',
                  icon: Coins,
                  testId: 'credit-low-banner',
                  title: `Low balance — $${creditBalance !== null ? Number(creditBalance).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '0.00'} remaining`,
                  body: 'Services will pause automatically at $0. Top up to avoid interruption.',
                  cta: { label: 'Add Balance', testId: 'credit-add-btn-low',
                         onClick: () => navigate(`${basePath}/billing`) },
                  dismiss: () => setCreditBannerDismissed(true),
                });
              }
              if (balanceStatus?.low_balance && !balanceStatus?.calls_blocked && !balanceBannerDismissed) {
                const prov = (balanceStatus.provider || 'Provider').charAt(0).toUpperCase() + (balanceStatus.provider || '').slice(1);
                alerts.push({
                  id: 'provider-low',
                  level: 'warn',
                  icon: AlertTriangle,
                  testId: 'balance-low-banner',
                  title: `Low ${prov} balance — $${(balanceStatus.balance ?? 0).toFixed(2)}`,
                  body: 'Calls will stop when the provider balance hits $0.',
                  cta: balanceStatus.top_up_url ? {
                    label: 'Top Up',
                    testId: 'balance-topup-btn-low',
                    href: balanceStatus.top_up_url,
                  } : null,
                  dismiss: () => setBalanceBannerDismissed(true),
                });
              }

              if (alerts.length === 0) return null;
              const worstLevel = alerts.some(a => a.level === 'critical') ? 'critical' : 'warn';
              const isCrit = worstLevel === 'critical';
              const wrapBorder = isCrit
                ? (isDarkMode ? 'border-red-500/30 bg-red-500/[0.06]' : 'border-red-300 bg-red-50')
                : (isDarkMode ? 'border-amber-400/20 bg-amber-400/[0.05]' : 'border-amber-300 bg-amber-50');
              return (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className={`border-b ${wrapBorder}`}
                  data-testid="unified-balance-banner"
                >
                  <div className="px-4 py-2 flex flex-col gap-1.5">
                    {alerts.map((a) => {
                      const Icon = a.icon;
                      const rowCrit = a.level === 'critical';
                      const iconColor = rowCrit ? 'text-red-500' : (isDarkMode ? 'text-amber-400' : 'text-amber-600');
                      const titleColor = rowCrit
                        ? (isDarkMode ? 'text-red-400' : 'text-red-700')
                        : (isDarkMode ? 'text-amber-300' : 'text-amber-800');
                      const bodyColor = rowCrit
                        ? (isDarkMode ? 'text-red-300/80' : 'text-red-700/80')
                        : (isDarkMode ? 'text-amber-300/70' : 'text-amber-800/70');
                      const ctaBg = rowCrit
                        ? (isDarkMode ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-red-100 text-red-700 hover:bg-red-200')
                        : (isDarkMode ? 'bg-amber-400/15 text-amber-400 hover:bg-amber-400/25' : 'bg-amber-100 text-amber-800 hover:bg-amber-200');
                      return (
                        <div key={a.id} className="flex items-start gap-2.5" data-testid={a.testId}>
                          <Icon className={`shrink-0 mt-0.5 ${iconColor}`} size={15} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-semibold ${titleColor} leading-snug`}>
                              {a.title}
                            </p>
                            {a.body && (
                              <p className={`text-[11px] ${bodyColor} leading-snug mt-0.5`}>
                                {a.body}
                              </p>
                            )}
                          </div>
                          {a.cta && (a.cta.href ? (
                            <a
                              href={a.cta.href}
                              target="_blank"
                              rel="noreferrer"
                              className={`shrink-0 flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-semibold transition ${ctaBg}`}
                              data-testid={a.cta.testId}
                            >
                              {a.cta.label} <ExternalLink size={11} />
                            </a>
                          ) : (
                            <button
                              onClick={a.cta.onClick}
                              className={`shrink-0 flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-semibold transition ${ctaBg}`}
                              data-testid={a.cta.testId}
                            >
                              {a.cta.label}
                            </button>
                          ))}
                          {a.dismiss && (
                            <button
                              onClick={a.dismiss}
                              className={`shrink-0 p-1 rounded transition ${rowCrit ? 'text-red-400/60 hover:text-red-400' : (isDarkMode ? 'text-amber-400/50 hover:text-amber-400 hover:bg-amber-400/10' : 'text-amber-600/50 hover:text-amber-800 hover:bg-amber-100')}`}
                              data-testid={`dismiss-${a.id}`}
                              aria-label="Dismiss"
                            >
                              <X size={13} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              );
            })()}
          </AnimatePresence>
          <div ref={contentRef} className={`flex-1 min-h-0 ${isInbox || fillContent ? 'flex flex-col overflow-hidden' : 'overflow-auto'}`} data-testid="subaccount-content-viewport" data-scroll-mode={isInbox || fillContent ? 'contained' : 'page'}>
            {children}
          </div>
        </main>
      </div>
    </SubAccountContext.Provider>
  );
};

export default SubAccountLayout;
